import * as THREE from "three";
import { SceneManager } from "../core/scene_manager.js";

interface PixelFeatureBackup
{
  clearcoat?: number;
  normalMap?: THREE.Texture | null;
  emissiveMap?: THREE.Texture | null;
  emissive?: THREE.Color;
}

const backup = new WeakMap<THREE.Material, PixelFeatureBackup>();

export const PixelFeatures = {
  clearcoat: true,
  normal_maps: true,
  emissive_maps: true,
};

function applyToMaterial( material: any ): void
{
  // The block families use Phong/Lambert materials, so limiting this debug
  // switch to PBR materials made NORMAL MAPS appear to toggle while leaving
  // the suspect blocks unchanged.
  if ( !( "normalMap" in material ) && !( "emissiveMap" in material ) && !( "clearcoat" in material ) )
    return;

  if ( !backup.has( material ) )
    backup.set( material, {
      clearcoat: material.clearcoat,
      normalMap: material.normalMap ?? null,
      emissiveMap: material.emissiveMap ?? null,
      emissive: material.emissive ? material.emissive.clone() : undefined,
    } );

  const saved = backup.get( material )!;
  let dirty = false;

  const wanted_clearcoat = PixelFeatures.clearcoat ? ( saved.clearcoat ?? 0 ) : 0;
  if ( material.clearcoat !== undefined && material.clearcoat !== wanted_clearcoat )
  {
    material.clearcoat = wanted_clearcoat;
    dirty = true;
  }

  const wanted_normal = PixelFeatures.normal_maps ? ( saved.normalMap ?? null ) : null;
  if ( material.normalMap !== wanted_normal )
  {
    material.normalMap = wanted_normal;
    dirty = true;
  }

  const wanted_emissive_map = PixelFeatures.emissive_maps ? ( saved.emissiveMap ?? null ) : null;
  if ( material.emissiveMap !== wanted_emissive_map )
  {
    material.emissiveMap = wanted_emissive_map;
    dirty = true;
  }

  if ( material.emissive && saved.emissive )
  {
    if ( PixelFeatures.emissive_maps )
    {
      if ( !material.emissive.equals( saved.emissive ) )
      {
        material.emissive.copy( saved.emissive );
        dirty = true;
      }
    }
    else if ( material.emissive.r !== 0 || material.emissive.g !== 0 || material.emissive.b !== 0 )
    {
      material.emissive.setRGB( 0, 0, 0 );
      dirty = true;
    }
  }

  if ( dirty )
    material.needsUpdate = true;
}

export function applyPixelFeatures(): void
{
  SceneManager.world?.traverse( object =>
  {
    const material = ( object as THREE.Mesh ).material;
    if ( !material )
      return;

    for ( const entry of Array.isArray( material ) ? material : [ material ] )
      applyToMaterial( entry );
  } );
}
